import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/shared/hooks/use-toast";
import { logger } from '@/shared/utils/logger';
import { setImpersonationInProgress } from './useAuthState';

interface ImpersonationState {
  isImpersonating: boolean;
  sessionId: string | null;
  targetUserId: string | null;
  targetUserName: string | null;
  targetUserEmail: string | null;
  adminEmail: string | null;
  expiresAt: Date | null;
}

interface ImpersonationContextType extends ImpersonationState {
  setImpersonationData: (data: Omit<ImpersonationState, 'isImpersonating'>) => void;
  endImpersonation: () => Promise<void>;
  clearImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const STORAGE_KEY = 'impersonation_session';

/**
 * @description Context provider that manages user impersonation state. Persists session to localStorage,
 * checks expiry periodically, and provides methods to start/end impersonation sessions.
 * @param {{ children: ReactNode }} props - React children to wrap.
 */
export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ImpersonationState>({
    isImpersonating: false,
    sessionId: null,
    targetUserId: null,
    targetUserName: null,
    targetUserEmail: null,
    adminEmail: null,
    expiresAt: null
  });

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const expiresAt = new Date(data.expiresAt);

        // Check if session has expired
        if (expiresAt > new Date()) {
          setState({
            isImpersonating: true,
            sessionId: data.sessionId,
            targetUserId: data.targetUserId,
            targetUserName: data.targetUserName,
            targetUserEmail: data.targetUserEmail,
            adminEmail: data.adminEmail,
            expiresAt
          });
          logger.debug('🔐 Restored impersonation session:', data.sessionId);
        } else {
          // Session expired, clear it
          localStorage.removeItem(STORAGE_KEY);
          logger.debug('🔐 Impersonation session expired, cleared');
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Check for session expiry
  useEffect(() => {
    if (!state.isImpersonating || !state.expiresAt) return;

    const checkExpiry = () => {
      if (state.expiresAt && state.expiresAt < new Date()) {
        toast({
          title: "Sessione scaduta",
          description: "La sessione di impersonation è scaduta",
          variant: "destructive"
        });
        clearImpersonation();
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [state.isImpersonating, state.expiresAt]);

  const setImpersonationData = (data: Omit<ImpersonationState, 'isImpersonating'>) => {
    const newState = {
      isImpersonating: true,
      ...data
    };
    setState(newState);

    // Persist to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sessionId: data.sessionId,
      targetUserId: data.targetUserId,
      targetUserName: data.targetUserName,
      targetUserEmail: data.targetUserEmail,
      adminEmail: data.adminEmail,
      expiresAt: data.expiresAt?.toISOString()
    }));

    logger.debug('🔐 Impersonation session started:', data.sessionId);
  };

  const endImpersonation = async () => {
    if (!state.sessionId) return;

    try {
      // Update session in database
      const { error } = await supabase
        .from('impersonation_sessions')
        .update({
          is_active: false,
          ended_at: new Date().toISOString()
        })
        .eq('id', state.sessionId);

      if (error) {
        logger.error('Error ending impersonation session:', error);
      }

      // Log audit
      await supabase
        .from('impersonation_audit_log')
        .insert({
          session_id: state.sessionId,
          admin_id: (await supabase.auth.getUser()).data.user?.id,
          target_user_id: state.targetUserId,
          action: 'impersonation_ended',
          details: {
            ended_by: 'user_action',
            duration_minutes: state.expiresAt
              ? Math.round((new Date().getTime() - (state.expiresAt.getTime() - 30 * 60 * 1000)) / 60000)
              : null
          }
        });

      // Try to restore admin session
      const adminSessionRaw = localStorage.getItem('impersonation_admin_session');
      clearImpersonation();

      if (adminSessionRaw) {
        try {
          const adminTokens = JSON.parse(adminSessionRaw);
          localStorage.removeItem('impersonation_admin_session');

          // Restore admin session directly — no signOut needed,
          // setSession() overwrites the current impersonated session.
          const { error: restoreError } = await supabase.auth.setSession({
            access_token: adminTokens.access_token,
            refresh_token: adminTokens.refresh_token,
          });

          if (!restoreError) {
            toast({
              title: "Sessione terminata",
              description: "Sei tornato al tuo account admin"
            });
            window.location.href = '/admin/users';
            return;
          }
          logger.warn('Failed to restore admin session, falling back to login:', restoreError);
        } catch (e) {
          setImpersonationInProgress(false);
          logger.warn('Failed to parse admin session:', e);
        }
      }

      // Fallback: sign out and redirect to auth
      localStorage.removeItem('impersonation_admin_session');
      await supabase.auth.signOut();
      toast({
        title: "Sessione terminata",
        description: "Hai terminato la sessione di impersonation"
      });
      window.location.href = '/auth';

    } catch (error) {
      logger.error('Error ending impersonation:', error);
      toast({
        title: "Errore",
        description: "Errore durante la terminazione della sessione",
        variant: "destructive"
      });
    }
  };

  const clearImpersonation = () => {
    setState({
      isImpersonating: false,
      sessionId: null,
      targetUserId: null,
      targetUserName: null,
      targetUserEmail: null,
      adminEmail: null,
      expiresAt: null
    });
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('impersonation_admin_session');
    logger.debug('🔐 Impersonation session cleared');
  };

  return (
    <ImpersonationContext.Provider value={{
      ...state,
      setImpersonationData,
      endImpersonation,
      clearImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

/**
 * @description Hook to access impersonation state and actions from ImpersonationContext.
 * @returns {{ isImpersonating: boolean, sessionId: string | null, targetUserId: string | null, targetUserName: string | null, targetUserEmail: string | null, adminEmail: string | null, expiresAt: Date | null, setImpersonationData: Function, endImpersonation: Function, clearImpersonation: Function }}
 */
export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error("useImpersonation deve essere utilizzato all'interno di un ImpersonationProvider");
  }
  return context;
};
